import { useEffect, useState } from 'react'
import * as React from 'react'

/* Helpers */
import { getTokensFor, HashClaim, TokenInfo, tokensQuantityByEventId, getENSFromAddress } from '../../api'
import { getNumberWithOrdinal, isValidEmail } from '../../lib/helpers'

/* Components */
import { LinkButton } from '../../components/LinkButton'
import ClaimFooterMessage from './ClaimFooterMessage'
import { analyticsTracking } from 'lib/analytics/analytics'
import { Link } from 'react-router-dom'
import { Loading } from '../../components/Loading'
import {
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share'
import { TWITTER_SHARE_BASE_LINK } from 'lib/constants'

/*
 * @dev: Component to show minted token
 * */
const ClaimFinished: React.FC<{ claim: HashClaim }> = ({ claim }) => {
  const isNoCoinerFlowEnabled: boolean = !!claim.event.minting_config?.available_wallet_providers?.length
  const claimedWithEmail = !!(
    claim &&
    claim.claimed &&
    claim.user_input &&
    isValidEmail(claim.user_input) &&
    !isNoCoinerFlowEnabled
  )
  const isNoCoinerAndClaimedWithEmail = isNoCoinerFlowEnabled && claim.user_input && isValidEmail(claim.user_input)
  const collectionAddress = claim.beneficiary ? claim.beneficiary : claim.user_input

  const [tokens, setTokens] = useState<TokenInfo[] | null>(null)
  const [currentToken, setCurrentToken] = useState<TokenInfo | undefined>(undefined)
  const [collectorNumber, setCollectorNumber] = useState<string | null>(null)
  const [ens, setEns] = useState<string | null>(null)

  useEffect(() => {
    getEvents().then()
    getCollectorNumber().then()
    getEnsAddress(collectionAddress).then()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const getEnsAddress = async (address: string | null) => {
    try {
      if (isNoCoinerFlowEnabled) {
        setEns(claim.user_input)
      } else if (address) {
        const ensFromAddress = await getENSFromAddress(address)
        setEns(ensFromAddress.valid ? ensFromAddress.ens : address)
      }
    } catch (e) {
      setEns(address)
      console.log(e)
    }
  }

  const appLink = claimedWithEmail ? `/scan/${claim.user_input}` : `/scan/${claim.beneficiary}`

  const getEvents = async () => {
    try {
      let tokens = null
      // use the user_input ( email ) if there is no beneficiary ( address ) to get collection
      if (collectionAddress) {
        tokens = await getTokensFor(collectionAddress)
      }
      setTokens(tokens)
      if (tokens) {
        const currentToken = tokens.find((token) => token.event.id === claim.event_id)
        setCurrentToken(currentToken)
      }
    } catch (e) {
      console.log(e)
    }
  }

  const getCollectorNumber = async () => {
    try {
      const number = await tokensQuantityByEventId(claim.event_id)
      setCollectorNumber(getNumberWithOrdinal(number))
    } catch (e) {
      console.log(e)
    }
  }

  const tokensMessage = () => {
    if (tokens) {
      const tokensQuantity = getNumberWithOrdinal(tokens.length)
      return `You just minted your ${tokensQuantity} POAP 🎉`
    }
    return null
  }

  const poapGalleryUrl = () => {
    return `https://poap.gallery/event/${claim.event_id}`
  }

  return (
    <div className={'claim-info'} data-aos="fade-up" data-aos-delay="300">
      <div className={'info-title'}>
        Congratulations! <br />
        {claim.event.name} POAP was added to collection: <br />
        {ens && (
          <Link className="collection-link" to={appLink}>
            {ens}
          </Link>
        )}
        {!ens && <Loading />}
      </div>
      <div className={'text-info'}>
        <>
          {analyticsTracking.claim.trackSuccessClaim()}
          {tokensMessage()}
          <br />
        </>
      </div>
      {isNoCoinerAndClaimedWithEmail && (
        <div className="no-coiner-starter-wallet">
          <div className="container">
            <p className="starter-wallet-title">This POAP has been minted to a starter wallet</p>
            <span>
              Once you access this wallet, you will be able to use the address to collect more POAPs or transfer your
              POAP to another account
            </span>
          </div>
        </div>
      )}
      <LinkButton
        text={isNoCoinerAndClaimedWithEmail ? 'Access your wallet' : 'Browse collection'}
        link={
          isNoCoinerAndClaimedWithEmail
            ? `${process.env.REACT_APP_COLLECTORS_DOMAIN}/wallet/access?address=${collectionAddress}`
            : appLink
        }
        extraClass={'link-btn'}
        target={isNoCoinerAndClaimedWithEmail ? '_blank' : ''}
        onClick={() => {
          !isNoCoinerAndClaimedWithEmail && analyticsTracking.claim.trackViewCollection()
        }}
      />
      <div className="content-event">
        <div className="minted-message">
          {collectorNumber && (
            <span>
              You were the {collectorNumber} collector to mint this POAP.
              <br />
              See who else shared this moment at{' '}
              <a href={poapGalleryUrl()} onClick={() => analyticsTracking.claim.trackViewInGallery()}>
                POAP Gallery
              </a>
              .
            </span>
          )}
          {collectorNumber && <br />}
        </div>
        {currentToken?.tokenId && (
          <>
            <h2>Tell your friends!</h2>
            <ul className="social-icons">
              <li>
                <TwitterShareButton
                  url={window.location.host.toString() + `/token/${currentToken.tokenId}`}
                  title={`Look at my ${claim.event.name} POAP NFT!`}
                  via="poapxyz"
                  openWindow={false}
                  onClick={(link: string) => {
                    const queryParams = link.split('?')[1]
                    const twitterShareLink = TWITTER_SHARE_BASE_LINK + '?' + queryParams
                    window.open(twitterShareLink)
                  }}
                >
                  <TwitterIcon size={40} round iconBgStyle={{ fill: '#6534FF' }} />
                </TwitterShareButton>
              </li>
              <li>
                <TelegramShareButton
                  url={window.location.host.toString() + `/token/${currentToken.tokenId}`}
                  title={`Look at my ${claim.event.name} POAP NFT!`}
                >
                  <TelegramIcon size={40} round iconBgStyle={{ fill: '#6534FF' }} />
                </TelegramShareButton>
              </li>
              <li>
                <RedditShareButton
                  url={window.location.host.toString() + `/token/${currentToken.tokenId}`}
                  title={`Look at my ${claim.event.name} POAP NFT!`}
                >
                  <RedditIcon size={40} round iconBgStyle={{ fill: '#6534FF' }} />
                </RedditShareButton>
              </li>
            </ul>
          </>
        )}
      </div>
      <ClaimFooterMessage />
    </div>
  )
}

export default ClaimFinished
